import React, { useState } from 'react'
import Input from 'components/input'
import SortMenu, { SortSection, SortValue } from 'components/sortMenu'
import { tv } from 'tailwind-variants'

const userSelectStyles = tv({
  slots: {
    input: 'mb-2',
    list: 'overflow-auto border border-divider rounded-lg max-h-[200px]',
    row: 'flex items-center p-2.5 hover:bg-surface-muted border-b border-divider last:border-b-0',
    empty: 'p-4 text-center text-sm text-muted',
    name: 'text-sm font-medium',
    email: 'text-xs text-muted'
  }
})

export interface UserOption {
  publicId: string
  name: string
  email: string
  createdAt?: string
  [key: string]: unknown
}

type UserSortBy = 'name' | 'createdAt'

const USER_SORT_SECTIONS: SortSection<UserSortBy>[] = [
  {
    label: 'Name',
    options: [
      { by: 'name', direction: 'asc', label: 'A → Z' },
      { by: 'name', direction: 'desc', label: 'Z → A' }
    ]
  },
  {
    label: 'Date joined',
    options: [
      { by: 'createdAt', direction: 'desc', label: 'Newest first' },
      { by: 'createdAt', direction: 'asc', label: 'Oldest first' }
    ]
  }
]

const DEFAULT_USER_SORT: SortValue<UserSortBy> = { by: 'name', direction: 'asc' }

/** "N selected" badge for the label above a UserSelect; renders nothing when none are selected */
export const SelectedCount: React.FC<{ count: number }> = ({ count }) =>
  count > 0 ? <small className="font-semibold text-blue ml-1">{count} selected</small> : null

interface UserSelectProps {
  users: UserOption[]
  selectedEmails?: string[]
  disabledEmails?: string[]
  pinnedEmails?: string[]
  hideSearch?: boolean
  showToolbar?: boolean
  onToggle?: (email: string) => void
  onRemove?: (email: string) => void
  renderDetail?: (user: UserOption) => React.ReactNode
  placeholder?: string
  emptyMessage?: string
  classNames?: {
    input?: string
    list?: string
    row?: string
    empty?: string
    name?: string
    email?: string
  }
}

const UserSelect: React.FC<UserSelectProps> = ({
  users,
  selectedEmails,
  disabledEmails,
  pinnedEmails,
  hideSearch,
  showToolbar,
  onToggle,
  onRemove,
  renderDetail,
  placeholder = 'Search users...',
  emptyMessage = 'No users match your search.',
  classNames
}) => {
  const [search, setSearch] = useState<string>('')
  const [sort, setSort] = useState<SortValue<UserSortBy>>(DEFAULT_USER_SORT)
  const { input, list, row, empty, name, email } = userSelectStyles()

  const selectedSet = new Set(selectedEmails ?? [])
  const disabledSet = new Set(disabledEmails ?? [])
  const pinnedSet = new Set(pinnedEmails ?? [])

  const sortDirection = sort.direction === 'asc' ? 1 : -1
  const getCreatedAtTimestamp = (createdAt?: string): number => {
    if (!createdAt) return 0

    const timestamp = new Date(createdAt).getTime()
    return Number.isFinite(timestamp) ? timestamp : 0
  }

  const compareUsers = (a: UserOption, b: UserOption): number => {
    if (sort.by === 'createdAt') {
      const aTime = getCreatedAtTimestamp(a.createdAt)
      const bTime = getCreatedAtTimestamp(b.createdAt)
      if (aTime !== bTime) return sortDirection * (aTime - bTime)
    }
    return sortDirection * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  }
  const sorted = [...users].sort((a, b) => {
    const aPinned = pinnedSet.has(a.email) ? 0 : 1
    const bPinned = pinnedSet.has(b.email) ? 0 : 1
    if (aPinned !== bPinned) return aPinned - bPinned
    const aDisabled = disabledSet.has(a.email) ? 0 : 1
    const bDisabled = disabledSet.has(b.email) ? 0 : 1
    if (aDisabled !== bDisabled) return aDisabled - bDisabled
    const aSelected = selectedSet.has(a.email) ? 0 : 1
    const bSelected = selectedSet.has(b.email) ? 0 : 1
    if (aSelected !== bSelected) return aSelected - bSelected
    return compareUsers(a, b)
  })

  const filtered = search
    ? sorted.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase())
      )
    : sorted

  return (
    <>
      {!hideSearch && (
        <div className="flex items-center gap-2 mb-2">
          <Input
            type="text"
            className={input({ class: `flex-1 !mb-0 ${classNames?.input ?? ''}` })}
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {showToolbar && <SortMenu value={sort} onChange={setSort} sections={USER_SORT_SECTIONS} />}
        </div>
      )}
      <div className={list({ class: classNames?.list })}>
        {filtered.length === 0 ? (
          <div className={empty({ class: classNames?.empty })}>{emptyMessage}</div>
        ) : (
          filtered.map((user) => {
            const isDisabled = disabledSet.has(user.email)
            const isClickable = !isDisabled && !onRemove
            return (
              <div
                key={user.email}
                className={row({ class: `${isClickable ? 'cursor-pointer' : ''} ${classNames?.row ?? ''}` })}
                onClick={() => isClickable && onToggle?.(user.email)}
              >
                <div className="flex items-center gap-2 w-full min-w-0">
                  {!isDisabled && selectedEmails && (
                    <input
                      type="checkbox"
                      className="rounded-md border-2 border-divider !size-4"
                      checked={selectedSet.has(user.email)}
                      aria-label={user.name}
                      readOnly
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1">
                      <span className={name({ class: `whitespace-nowrap ${classNames?.name ?? ''}` })}>
                        {user.name}
                      </span>
                      <span className={email({ class: `truncate ${classNames?.email ?? ''}` })}>- {user.email}</span>
                    </div>
                    {renderDetail?.(user)}
                  </div>
                  {onRemove && (
                    <div
                      className="ml-auto text-xl text-muted hover:text-body cursor-pointer shrink-0 px-1"
                      onClick={() => onRemove(user.email)}
                    >
                      &times;
                    </div>
                  )}
                </div>
              </div>
            )
          })
        )}
      </div>
    </>
  )
}

export default UserSelect
